import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@contexts/ThemeContext';
import { B2BViewProvider } from '@contexts/B2BViewContext';
import { QueryProvider } from '@core/query/QueryProvider';
import { AuthProvider } from '@core/auth/AuthProvider';
import { OrgGuard } from '@core/auth/OrgGuard';
import { OnboardingGate } from '@core/auth/OnboardingGate';
import { RouteAccessOutletGuard, StandaloneRouteAccessBoundary } from '@core/auth/RouteAccessGuard';
import { AppLayout } from '@shared/components/Layout/AppLayout';
import { ToastProvider } from '@shared/components/Toast/ToastProvider';

// Marketing landing page
import { B2BLandingPage } from '@modules/public/landing/B2BLandingPage';
import { B2BDemoPage } from '@modules/public/landing/B2BDemoPage';

// Marketing — Product
import { ProductTourPage } from '@modules/public/marketing/ProductTourPage';
import { FeaturesPage }    from '@modules/public/marketing/FeaturesPage';

// Marketing — Software features
import { CandidatesPipelinePage } from '@modules/public/marketing/software/CandidatesPipelinePage';
import { AiInterviewsPage as SoftwareAiInterviewsPage } from '@modules/public/marketing/software/AiInterviewsPage';
import { RoleFitPage as SoftwareRoleFitPage } from '@modules/public/marketing/software/RoleFitPage';
import { WorkflowEnginePage }     from '@modules/public/marketing/software/WorkflowEnginePage';
import { AgencyModePage }         from '@modules/public/marketing/software/AgencyModePage';

// Marketing — Resources
import { BlogPage }       from '@modules/public/marketing/resources/BlogPage';
import { FaqPage }        from '@modules/public/marketing/resources/FaqPage';
import { HelpCenterPage } from '@modules/public/marketing/resources/HelpCenterPage';

// Marketing — Affiliate Program
import { AffiliatePage } from '@modules/public/marketing/affiliate/AffiliatePage';

// Marketing — Company (Contact Us kept for Resources column)
import { ContactUsPage }     from '@modules/public/marketing/company/ContactUsPage';
import { GdprCompliancePage } from '@modules/public/marketing/company/GdprCompliancePage';

// Marketing — Legal
import { PrivacyPolicyPage }  from '@modules/public/marketing/legal/PrivacyPolicyPage';
import { TermsOfServicePage } from '@modules/public/marketing/legal/TermsOfServicePage';
import { CookiePolicyPage }   from '@modules/public/marketing/legal/CookiePolicyPage';
import { TrustCenterPage }    from '@modules/public/marketing/legal/TrustCenterPage';

// Marketing — Compare
import { VsGreenhousePage }    from '@modules/public/marketing/compare/VsGreenhousePage';
import { VsHireVuePage }       from '@modules/public/marketing/compare/VsHireVuePage';
import { VsLeverPage }         from '@modules/public/marketing/compare/VsLeverPage';
import { VsVincerePage }       from '@modules/public/marketing/compare/VsVincerePage';
import { VsISmartRecruitPage } from '@modules/public/marketing/compare/VsISmartRecruitPage';

/**
 * AppRoot — OrgGuard + app shell outlet. Marketing lives at `/business` (public route).
 */
function AppRoot() {
  return (
    <OrgGuard>
      <Outlet />
    </OrgGuard>
  );
}

// Public routes (no auth)
import { RespondPage } from '@modules/public/respond/RespondPage';
import { TrackPage } from '@modules/public/track/TrackPage';
import { CareersLandingPage } from '@modules/public/careers/CareersLandingPage';
import { CareersOrgPage } from '@modules/public/careers/CareersOrgPage';
import { CareerApplicationStatusPage } from '@modules/public/careers/CareerApplicationStatusPage';
import { AiInterviewPage } from '@modules/public/ai-interview/AiInterviewPage';
import { InterviewConfigurePage } from '@modules/public/ai-interview/InterviewConfigurePage';
import { InterviewPrepPage } from '@modules/public/ai-interview/InterviewPrepPage';

// Onboarding
import { OnboardingLayout } from '@modules/onboarding/OnboardingLayout';
import { OnboardingStartStep } from '@modules/onboarding/OnboardingStartStep';
import { OnboardingWorkspaceStep } from '@modules/onboarding/OnboardingWorkspaceStep';
import { OnboardingTeamStep } from '@modules/onboarding/OnboardingTeamStep';

// Protected routes
import { DashboardPage } from '@modules/dashboard/DashboardPage';
import { HomePage } from '@modules/home/HomePage';
import { CandidatesPage } from '@modules/candidates/CandidatesPage';
import { CandidateNewPage } from '@modules/candidates/CandidateNewPage';
import { CandidateDetailPage } from '@modules/candidates/CandidateDetailPage';
import { ClientsPage } from '@modules/clients/ClientsPage';
import { ClientNewPage } from '@modules/clients/ClientNewPage';
import { ClientDetailPage } from '@modules/clients/ClientDetailPage';
import { JobsPage } from '@modules/jobs/JobsPage';
// JobNewPage is rendered as a modal (JobNewWizardModal) from RequisitionDetailPage
import { JobDetailPage } from '@modules/jobs/JobDetailPage';
import { RequisitionsPage } from '@modules/requisitions/RequisitionsPage';
import { RequisitionNewPage } from '@modules/requisitions/RequisitionNewPage';
import { RequisitionIntakeWizardPage } from '@modules/requisitions/RequisitionIntakeWizardPage';
import { RequisitionDetailPage } from '@modules/requisitions/RequisitionDetailPage';
import { ApplicationsPage } from '@modules/applications/ApplicationsPage';
import { ApplicationNewPage } from '@modules/applications/ApplicationNewPage';
import { ApplicationDetailPage } from '@modules/applications/ApplicationDetailPage';
import { EmailPage } from '@modules/email/EmailPage';
import { EmailThreadPage } from '@modules/email/EmailThreadPage';
import { EmailComposePage } from '@modules/email/EmailComposePage';
import { EmailSequencesPage } from '@modules/email/EmailSequencesPage';
import { ReportsPage } from '@modules/reports/ReportsPage';
import { TeamActivityPage } from '@modules/reports/TeamActivityPage';
import { ReportsTatPage } from '@modules/reports/ReportsTatPage';
import { TeamPage } from '@modules/team/TeamPage';
import { ProfilePage as TeamMemberProfilePage } from '@modules/team/ProfilePage';
import { SettingsPage } from '@modules/settings/SettingsPage';
import { SettingsLayout } from '@modules/settings/SettingsLayout';
import { OrgOffboardingPage } from '@modules/settings/OrgOffboardingPage';
import { ProfileSettingsPage } from '@modules/settings/ProfileSettingsPage';
import { OrgSettingsPage } from '@modules/settings/OrgSettingsPage';
import { OrgHierarchyPage } from '@modules/settings/OrgHierarchyPage';
import { RolesSettingsPage } from '@modules/settings/RolesSettingsPage';
import { AutomationSettingsPage } from '@modules/settings/AutomationSettingsPage';
import { IntegrationsPage } from '@modules/settings/IntegrationsPage';
import { MaskingTemplatesPage } from '@modules/settings/MaskingTemplatesPage';
import { EmailTemplatesSettingsPage } from '@modules/settings/EmailTemplatesSettingsPage';
import { BillingPage } from '@modules/settings/BillingPage';
import { SettingsTeamPage } from '@modules/settings/SettingsTeamPage';
import { CareerPageSettingsPage } from '@modules/settings/CareerPageSettingsPage';
import { BulkPage } from '@modules/bulk/BulkPage';
import { WorkflowsPage } from '@modules/workflows/WorkflowsPage';
import { SequencePage } from '@modules/workflows/SequencePage';
// Orchestrator removed from UI (Phase 5 cleanup) — backend tables/services retained
import { SchedulerPage } from '@modules/scheduler/SchedulerPage';
import { SingleOptimizePage } from '@modules/single-optimize/SingleOptimizePage';
import { ActivitiesPage } from '@modules/activities/ActivitiesPage';
import { InterviewsHubPage } from '@modules/interviews/InterviewsHubPage';
import { AiInterviewsPage } from '@modules/ai-interviews/AiInterviewsPage';
import { AiInterviewSessionDetailPage } from '@modules/ai-interviews/AiInterviewSessionDetailPage';
import { InterviewApplicationDetailPage } from '@modules/interviews/InterviewApplicationDetailPage';
import { InterviewCandidateDetailPage } from '@modules/interviews/InterviewCandidateDetailPage';
import { InterviewsPage } from '@modules/interviews/InterviewsPage';
import { InterviewLivePage } from '@modules/interviews/InterviewLivePage';
import { CollaboratePage } from '@modules/collaborate/CollaboratePage';
import { CalendarNotesPage } from '@modules/calendar-notes/CalendarNotesPage';
import { RoleFitPage } from '@modules/role-fit/RoleFitPage';
// RoleFit AI flows (1:N, N:1, N:N) removed from UI (Phase 5 cleanup) — pair detail + backend retained
// import { RoleFitAiPage } from '@modules/role-fit/RoleFitAiPage';
import { RoleFitPairDetailPage } from '@modules/role-fit/RoleFitPairDetailPage';
import { SmartMatchIntakePage } from '@modules/smart-match/SmartMatchIntakePage';
import { AssistantIntakeStubPage } from '@modules/agent/components/AssistantIntakeStubPage';
import { SmartMatchCanvasPage } from '@modules/smart-match/SmartMatchCanvasPage';
import { SmartMatchStagesSandboxPage } from '@modules/smart-match/SmartMatchStagesSandboxPage';
import { SourcingIntakePage } from '@modules/sourcing/SourcingIntakePage';
import { SourcingCanvasPage } from '@modules/sourcing/SourcingCanvasPage';
import { ObjectHistoryPage } from '@modules/object-history/ObjectHistoryPage';
import { LibraryPage } from '@modules/library/LibraryPage';

// Dev-only pages — imported at module level so TypeScript type-checks them;
// the route is only registered when import.meta.env.DEV is true so the chunk
// is tree-shaken from the production build.
import { ToastsDevPage } from '@modules/dev/ToastsDevPage';

function B2BViewBridge({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <B2BViewProvider surface={pathname === '/business' || pathname.startsWith('/business/') ? 'landing' : 'app'}>
      {children}
    </B2BViewProvider>
  );
}

/** Phase 8 T-20 §24 — Candidate-facing booking landing. Availability for a specific slot is opened from the record detail. */
function BookingDemoStubPage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center dark:bg-zinc-950">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10">
        <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
      </div>
      <div className="max-w-md">
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          Interview booking
        </h1>
        <p className="mt-2 text-zinc-500 dark:text-zinc-400">
          Candidate scheduling is available in the record detail.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
      <QueryProvider>
      <BrowserRouter>
        <B2BViewBridge>
        <AuthProvider>
        <Routes>
          {/* ── Public routes (no auth needed) ── */}
          {/* Phase 8 T-20 §24 — candidate-facing booking landing; real slot picker lives inside the record detail. */}
          <Route path="/book/demo" element={<BookingDemoStubPage />} />
          <Route path="/book/:handle/:slug" element={<BookingDemoStubPage />} />
          <Route path="/respond/:token" element={<RespondPage />} />
          <Route path="/track/:token" element={<TrackPage />} />
          <Route path="/careers" element={<CareersLandingPage />} />
          <Route path="/careers/status/:token" element={<CareerApplicationStatusPage />} />
          <Route path="/careers/:orgSlug" element={<CareersOrgPage />} />
          <Route path="/interview/prep/:token" element={<InterviewPrepPage />} />
          <Route path="/interview/configure/:token" element={<InterviewConfigurePage />} />
          <Route path="/interview/:token" element={<AiInterviewPage />} />

          {/* ── Onboarding — legacy bare path redirects to 3-step flow ── */}
          <Route path="/onboarding" element={<Navigate to="/onboarding/start" replace />} />

          {/* ── Phase 8 §14 Wrangle-style 3-step onboarding (post-signup) ── */}
          <Route path="/onboarding" element={<OnboardingLayout />}>
            <Route path="start"     element={<OnboardingStartStep />} />
            <Route path="workspace" element={<OnboardingWorkspaceStep />} />
            <Route path="team"      element={<OnboardingTeamStep />} />
          </Route>

          {/* B2B marketing (public) — more specific paths first */}
          <Route path="/business/demo" element={<B2BDemoPage />} />

          {/* Product */}
          <Route path="/business/product-tour" element={<ProductTourPage />} />
          <Route path="/business/features"     element={<FeaturesPage />} />

          {/* Software features */}
          <Route path="/business/software/candidates-pipeline" element={<CandidatesPipelinePage />} />
          <Route path="/business/software/ai-interviews"       element={<SoftwareAiInterviewsPage />} />
          <Route path="/business/software/role-fit"            element={<SoftwareRoleFitPage />} />
          <Route path="/business/software/workflow-engine"     element={<WorkflowEnginePage />} />
          <Route path="/business/software/agency-mode"         element={<AgencyModePage />} />

          {/* Resources */}
          <Route path="/business/blog"         element={<BlogPage />} />
          <Route path="/business/faq"          element={<FaqPage />} />
          <Route path="/business/help-center"  element={<HelpCenterPage />} />
          <Route path="/business/contact-us"   element={<ContactUsPage />} />

          {/* Affiliate */}
          <Route path="/business/affiliate" element={<AffiliatePage />} />

          {/* Legal */}
          <Route path="/business/privacy-policy"   element={<PrivacyPolicyPage />} />
          <Route path="/business/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/business/cookie-policy"    element={<CookiePolicyPage />} />
          <Route path="/business/gdpr-compliance"  element={<GdprCompliancePage />} />
          <Route path="/business/trust-center"     element={<TrustCenterPage />} />

          {/* Compare */}
          <Route path="/business/compare/greenhouse"    element={<VsGreenhousePage />} />
          <Route path="/business/compare/hirevue"       element={<VsHireVuePage />} />
          <Route path="/business/compare/lever"         element={<VsLeverPage />} />
          <Route path="/business/compare/vincere"       element={<VsVincerePage />} />
          <Route path="/business/compare/ismartrecruit" element={<VsISmartRecruitPage />} />

          <Route path="/business" element={<B2BLandingPage />} />

          {/*
           * "/" must redirect WITHOUT OrgGuard. Nesting index <Navigate /> under path="/" + AppRoot
           * mounted OrgGuard first and sent anonymous users to Keycloak before the redirect ran.
           */}
          <Route path="/" element={<Navigate to="/business" replace />} />

          {/* App shell: pathless layout — /dashboard, /candidates, … = OrgGuard-protected */}
          <Route element={<AppRoot />}>
            {/* Full-viewport flows (no sidebar / top bar) */}
            <Route
              path="clients/new"
              element={
                <StandaloneRouteAccessBoundary>
                  <ClientNewPage />
                </StandaloneRouteAccessBoundary>
              }
            />
            <Route
              path="settings/offboarding"
              element={
                <StandaloneRouteAccessBoundary>
                  <OrgOffboardingPage />
                </StandaloneRouteAccessBoundary>
              }
            />
            <Route element={<AppLayout />}>
            <Route element={<RouteAccessOutletGuard />}>
            <Route path="home" element={<HomePage />} />
            <Route path="dashboard" element={<DashboardPage />} />

            <Route path="candidates" element={<CandidatesPage />} />
            <Route path="candidates/new" element={<CandidateNewPage />} />
            <Route path="candidates/:id" element={<CandidateDetailPage />} />

            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientDetailPage />} />

            <Route path="jobs" element={<JobsPage />} />
            <Route path="jobs/:id" element={<JobDetailPage />} />

            <Route path="requisitions" element={<RequisitionsPage />} />
            <Route path="requisitions/new" element={<RequisitionNewPage />} />
            <Route path="requisitions/:id/intake" element={<RequisitionIntakeWizardPage />} />
            <Route path="requisitions/:id" element={<RequisitionDetailPage />} />

            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="applications/new" element={<ApplicationNewPage />} />
            <Route path="applications/:id" element={<ApplicationDetailPage />} />

            <Route path="emails" element={<EmailPage />} />
            <Route path="emails/thread/:id" element={<EmailThreadPage />} />
            <Route path="emails/compose" element={<EmailComposePage />} />
            <Route path="emails/sequences" element={<EmailSequencesPage />} />
            <Route path="emails/sequences/:id" element={<SequencePage />} />
            <Route path="email" element={<Navigate to="/emails" replace />} />
            <Route path="email/compose" element={<Navigate to="/emails/compose" replace />} />

            <Route path="bulk" element={<BulkPage />} />
            <Route path="orchestrator" element={<Navigate to="/smart-match" replace />} />
            <Route path="workflows" element={<WorkflowsPage />} />
            <Route path="scheduler" element={<SchedulerPage />} />
            <Route path="single-optimize" element={<SingleOptimizePage />} />
            <Route path="smart-match" element={<Navigate to="/smart-match/new" replace />} />
            <Route path="smart-match/new" element={<SmartMatchIntakePage />} />
            <Route path="smart-match/:pipelineId" element={<SmartMatchCanvasPage />} />
            {/* §29 — dev-only sandbox to step through the four stages */}
            <Route path="dev/smart-match-stages" element={<SmartMatchStagesSandboxPage />} />

            {/* Addendum F §33 — Sourcing (Wrangle). /sourcing is a thin shell
                that opens in the empty state; threaded chats hand off into the
                same MatchCanvasLayout Smart Match uses. */}
            <Route path="sourcing" element={<Navigate to="/sourcing/new" replace />} />
            <Route path="sourcing/new" element={<SourcingIntakePage />} />
            <Route path="sourcing/:threadId" element={<SourcingCanvasPage />} />

            {/* Phase 8 T-22 §27.B — Assistant intake routes; each entity's dedicated wizard replaces the generic intake as it ships. */}
            <Route path="jobs/new" element={<AssistantIntakeStubPage />} />
            <Route path="sequences/new" element={<AssistantIntakeStubPage />} />
            <Route path="interviews/new" element={<AssistantIntakeStubPage />} />
            <Route path="workflows/new" element={<AssistantIntakeStubPage />} />
            <Route path="role-fit/new" element={<AssistantIntakeStubPage />} />
            <Route path="single-optimize/new" element={<AssistantIntakeStubPage />} />
            <Route path="bulk/:action" element={<AssistantIntakeStubPage />} />
            <Route path="reports/ask" element={<AssistantIntakeStubPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="role-fit" element={<RoleFitPage />} />
            <Route path="role-fit-ai" element={<Navigate to="/smart-match" replace />} />
            <Route path="role-fit-ai/:flow" element={<Navigate to="/smart-match" replace />} />
            <Route path="role-fit/:candidateId/:jobId" element={<RoleFitPairDetailPage />} />
            <Route path="activities" element={<ActivitiesPage />} />
            <Route path="interviews/candidate/:candidateId" element={<InterviewCandidateDetailPage />} />
            <Route path="interviews/app/:applicationId" element={<InterviewApplicationDetailPage />} />
            <Route path="interviews" element={<InterviewsHubPage />} />
            <Route path="ai-interviews/:sessionId/live" element={<InterviewLivePage />} />
            <Route path="ai-interviews/:sessionId" element={<AiInterviewSessionDetailPage />} />
            <Route path="ai-interviews" element={<AiInterviewsPage />} />
            <Route path="interviews/prep" element={<InterviewsPage />} />
            <Route path="collaborate" element={<CollaboratePage />} />
            <Route path="calendar-notes" element={<CalendarNotesPage />} />

            <Route path="object-history" element={<ObjectHistoryPage />} />

            <Route path="reports" element={<ReportsPage />} />
            <Route path="reports/tat" element={<OrgGuard><ReportsTatPage /></OrgGuard>} />
            <Route path="reports/team" element={<OrgGuard><TeamActivityPage /></OrgGuard>} />

            <Route path="team" element={<TeamPage />} />
            <Route path="team/member/:id" element={<TeamPage />} />
            {/* Phase 8 · C-47 — RBAC-gated team member profile page. */}
            <Route path="team/:orgUserId" element={<TeamMemberProfilePage />} />

            {/* Phase 8 T-13 — Settings nested layout */}
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/profile" replace />} />
              <Route path="profile" element={<ProfileSettingsPage />} />
              <Route path="team" element={<SettingsTeamPage />} />
              <Route path="org-hierarchy" element={<Navigate to="/settings/team" replace />} />
              <Route path="roles" element={<RolesSettingsPage />} />
              <Route path="branding" element={<OrgSettingsPage />} />
              <Route path="organisation" element={<Navigate to="/settings/branding" replace />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="automation" element={<AutomationSettingsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="email-templates" element={<EmailTemplatesSettingsPage />} />
              <Route path="masking" element={<MaskingTemplatesPage />} />
              <Route path="career-page" element={<CareerPageSettingsPage />} />
            </Route>
            {/* Legacy catch-all — keep SettingsPage alive for any deep links not yet migrated */}
            <Route path="settings-legacy/*" element={<SettingsPage />} />
            </Route>
            </Route>
          </Route>

          {/* Dev-only toast preview — env-gated so never registers in prod. */}
          {import.meta.env.DEV ? (
            <Route element={<AppLayout />}>
              <Route path="dev/toasts" element={<ToastsDevPage />} />
            </Route>
          ) : null}

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        </AuthProvider>
        </B2BViewBridge>
      </BrowserRouter>
      </QueryProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
